/**
 * Probe product image URLs before showing them in the hero / live carts.
 * Failed URL keys are remembered for the session so broken items are skipped.
 */

const failedKeys = new Set();
const okKeys = new Set();

/** Stable key for a URL (host + path) — used as a light "hash" of identity. */
export function imageUrlKey(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}`.toLowerCase();
  } catch (_) {
    return url.split("?")[0].toLowerCase();
  }
}

export function markImageFailed(url) {
  const key = imageUrlKey(url);
  if (key) failedKeys.add(key);
}

export function isKnownBadImage(url) {
  const key = imageUrlKey(url);
  return !key || failedKeys.has(key);
}

function probeImage(url, timeoutMs = 4500) {
  const key = imageUrlKey(url);
  if (!key || failedKeys.has(key)) return Promise.resolve(false);
  if (okKeys.has(key)) return Promise.resolve(true);

  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      if (ok) okKeys.add(key);
      else failedKeys.add(key);
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    img.referrerPolicy = "no-referrer";
    img.decoding = "async";
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;
  });
}

/** First URL in the list that actually loads in the browser. */
export async function firstLoadableImage(urls) {
  const list = Array.isArray(urls) ? urls : [];
  for (const url of list) {
    if (!url || failedKeys.has(imageUrlKey(url))) continue;
    // eslint-disable-next-line no-await-in-loop
    if (await probeImage(url)) return url;
  }
  return null;
}

/**
 * Keep only products with at least one loadable image.
 * Probes in small parallel batches; drops items whose URLs all fail.
 */
export async function filterLoadableItems(items, count) {
  const want = Math.max(0, count || 0);
  const source = Array.isArray(items) ? items : [];
  const out = [];
  let cursor = 0;
  const BATCH = 6;

  while (out.length < want && cursor < source.length) {
    const batch = source.slice(cursor, cursor + BATCH);
    cursor += batch.length;

    const resolved = await Promise.all(
      batch.map(async (item) => {
        const urls =
          Array.isArray(item?.images) && item.images.length
            ? item.images
            : item?.image
              ? [item.image]
              : [];
        const working = await firstLoadableImage(urls);
        if (!working) return null;
        // Prefer the proven URL first; keep remaining untried as soft fallbacks
        const rest = urls.filter(
          (u) => u !== working && !failedKeys.has(imageUrlKey(u))
        );
        return {
          ...item,
          image: working,
          images: [working, ...rest],
        };
      })
    );

    for (const item of resolved) {
      if (!item) continue;
      out.push(item);
      if (out.length >= want) break;
    }
  }

  return out;
}
